import { useEffect, useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import api from '@/lib/api';
import type { LoginResponse } from '@/types/api';

interface SchoolSlugGuardProps {
  children: React.ReactNode;
}

/**
 * Keeps the school in the URL and the school in the session in agreement.
 *
 * For a single-school account this is what it always was: a slug that is not
 * yours is rewritten to the one that is. What changed is that a slug can now be
 * *another of your own schools* - a bookmark, or a link someone sent you - and
 * the right answer there is to switch to it rather than bounce the person back
 * to where they happened to be last.
 *
 * The switch is a server round trip, exactly as the sidebar switcher's is: the
 * URL is a request, never a grant. If the API refuses, the redirect below still
 * happens, so an unknown or revoked slug behaves as it did before.
 */
export function SchoolSlugGuard({ children }: SchoolSlugGuardProps) {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const user = useAuthStore((state) => state.user);
  const memberships = useAuthStore((state) => state.memberships);
  const switchSchool = useAuthStore((state) => state.switchSchool);
  const location = useLocation();
  const queryClient = useQueryClient();

  const mismatched = !!user && !!schoolSlug && user.schoolSlug !== schoolSlug;
  // Only a school this person is known to belong to is worth attempting. Anything
  // else is the old case and redirects without troubling the API.
  const target = mismatched
    ? memberships.find((membership) => membership.schoolSlug === schoolSlug)
    : undefined;

  // Keyed by the school that failed, not a bare boolean. A sticky flag would
  // mean one transient network error permanently disabled auto-switching for
  // this component: a later navigation to a different, perfectly good school
  // would fire the request and then immediately fall through to the redirect,
  // unmounting and cancelling it.
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const switchFailed = !!target && failedFor === target.schoolId;

  useEffect(() => {
    if (!target) return;
    let cancelled = false;

    (async () => {
      try {
        const response = await api.post<LoginResponse>('/api/auth/switch-school', {
          schoolId: target.schoolId,
        });
        if (cancelled) return;
        // Cleared before the session moves, so nothing cached under the previous
        // school can be rendered against this one.
        queryClient.clear();
        switchSchool(response.data.user, response.data.token);
      } catch {
        if (!cancelled) setFailedFor(target.schoolId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [target, queryClient, switchSchool]);

  // Mid-switch. Rendering the children here would run this school's screens with
  // the other school's token, so they wait.
  if (target && !switchFailed) return null;

  if (mismatched) {
    const pathWithoutSlug = location.pathname.replace(`/${schoolSlug}`, '');
    return <Navigate to={`/${user!.schoolSlug}${pathWithoutSlug}`} replace />;
  }

  return <>{children}</>;
}
